import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrophyIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { isSportsModuleEnabled } from '../config/modules';
import StudentSubscriptionPlans from './student/StudentSubscriptionPlans';
import TeacherSubscriptionPlans from './teacher/TeacherSubscriptionPlans';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const role = String(currentUser?.role || '').toUpperCase();
  const firstName = String(currentUser?.firstName || 'there').trim() || 'there';

  const primaryAction =
    role === 'TEACHER'
      ? { label: 'Open Teacher Dashboard', onClick: () => navigate('/teacher/dashboard') }
      : role === 'STUDENT'
        ? { label: 'Open Student Dashboard', onClick: () => navigate('/student/dashboard') }
        : role === 'SPORTS' && isSportsModuleEnabled
          ? { label: 'Open Sports Dashboard', onClick: () => navigate('/sports/dashboard') }
        : role === 'ADMIN'
          ? { label: 'Open Admin Operations', onClick: () => navigate('/admin/operations') }
          : null;

  const roleCards =
    role === 'TEACHER'
      ? [
          {
            title: 'Teaching workflow',
            description: 'Sessions, students, readiness, and earnings all route from your main dashboard.',
            icon: CalendarDaysIcon,
            to: '/teacher/dashboard',
            cta: 'Teacher dashboard',
          },
          {
            title: 'Exam creation',
            description: 'Manage tests and approvals without leaving the teacher workspace.',
            icon: AcademicCapIcon,
            to: '/teacher/exams',
            cta: 'Manage exams',
          },
          {
            title: 'Subscription control',
            description: 'Review plan coverage and move into payment or upgrades when needed.',
            icon: CreditCardIcon,
            to: '/subscriptions',
            cta: 'Manage plan',
          },
        ]
      : role === 'STUDENT'
        ? [
            {
              title: 'Learning dashboard',
              description: 'Track sessions, progress, attendance, and your next academic actions.',
              icon: UserGroupIcon,
              to: '/student/dashboard',
              cta: 'Student dashboard',
            },
            {
              title: 'Teacher discovery',
              description: 'Find local or online teachers and move directly into selection flow.',
              icon: SparklesIcon,
              to: '/student/find-teachers',
              cta: 'Find teachers',
            },
            {
              title: 'Payments and plans',
              description: 'Keep subscriptions, wallet usage, and plan changes in one place.',
              icon: CreditCardIcon,
              to: '/subscriptions',
              cta: 'Manage plan',
            },
          ]
        : role === 'SPORTS' && isSportsModuleEnabled
          ? [
              {
                title: 'Sports dashboard',
                description: 'Manage your player profile, teams, challenges, and tournament activity from one place.',
                icon: TrophyIcon,
                to: '/sports/dashboard',
                cta: 'Sports dashboard',
              },
              {
                title: 'Player registration',
                description: 'Create or update your sports profile without entering the student or teacher workflow.',
                icon: UserPlusIcon,
                to: '/sports/register',
                cta: 'Register player',
              },
              {
                title: 'Grounds and events',
                description: 'Find grounds, tournaments, teams, and challenge matches for local sports activity.',
                icon: MapPinIcon,
                to: '/sports/grounds',
                cta: 'Explore sports',
              },
            ]
          : role === 'ADMIN'
            ? [
                {
                  title: 'Reschedule approvals',
                  description: 'Review pending teacher and student requests that affect live sessions.',
                  icon: CalendarDaysIcon,
                  to: '/admin/reschedule-approvals',
                  cta: 'Open approvals',
                },
                {
                  title: 'Operations console',
                  description: 'Handle teacher verification, exams, disputes, and revenue monitoring.',
                  icon: ShieldCheckIcon,
                  to: '/admin/operations',
                  cta: 'Open operations',
                },
                {
                  title: 'Subscription control',
                  description: 'Access the shared subscription surface when you need payment context.',
                  icon: CreditCardIcon,
                  to: '/subscriptions',
                  cta: 'Open subscriptions',
                },
              ]
          : [
              {
                title: 'Account workspace',
                description: 'Use the main dashboard redirect to reach the correct workspace for this account.',
                icon: SparklesIcon,
                to: '/dashboard',
                cta: 'Open dashboard',
              },
            ];

  const roleLabel =
    role === 'TEACHER'
      ? 'Teacher workspace'
      : role === 'STUDENT'
        ? 'Student workspace'
        : role === 'SPORTS' && isSportsModuleEnabled
          ? 'Sports workspace'
          : role === 'ADMIN'
            ? 'Admin workspace'
            : 'Account workspace';
  const showSubscriptionAction = role === 'STUDENT' || role === 'TEACHER';

  return (
    <div className="space-y-8 text-slate-900">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.22),_transparent_26%),linear-gradient(135deg,_#020617_0%,_#0f172a_45%,_#0f766e_100%)] px-6 py-8 text-white shadow-[0_30px_90px_-52px_rgba(15,23,42,0.8)] sm:px-8 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_320px] lg:items-end">
          <div>
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">
              Legacy dashboard
            </span>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Welcome back, {firstName}. Use this page as the cleanest route into your active workspace.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
              This screen now acts as a gateway instead of a dead-end utility page. Jump into your role workflow, review plan access, and move forward without hunting through navigation.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {primaryAction ? (
                <button
                  onClick={primaryAction.onClick}
                  className="inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  {primaryAction.label}
                </button>
              ) : null}
              {showSubscriptionAction && (
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
                >
                  Manage subscriptions
                </Link>
              )}
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
            <div className="rounded-[1.25rem] border border-white/10 bg-slate-950/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Current access</p>
              <p className="mt-3 text-3xl font-semibold">{roleLabel}</p>
              <p className="mt-2 text-sm text-slate-300">
                {role ? `Signed in as ${role.toLowerCase()}.` : 'Signed in with a workspace account.'}
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                <p className="text-xs text-slate-300">Role</p>
                <p className="mt-2 text-xl font-semibold">{role || 'Unknown'}</p>
              </div>
              <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                <p className="text-xs text-slate-300">Account</p>
                <p className="mt-2 text-sm font-semibold break-all">{String(currentUser?.email || 'Signed in')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-3">
        {roleCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.title}
              to={card.to}
              className="group rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)] transition hover:-translate-y-0.5 hover:shadow-[0_28px_90px_-46px_rgba(15,23,42,0.46)]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <Icon className="h-6 w-6 text-slate-700" />
                </div>
                <ArrowRightIcon className="h-5 w-5 text-slate-300 transition group-hover:text-slate-700" />
              </div>
              <h2 className="mt-8 text-xl font-semibold tracking-tight text-slate-950">{card.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{card.description}</p>
              <p className="mt-6 text-sm font-semibold text-sky-700">{card.cta}</p>
            </Link>
          );
        })}
      </section>

      {(role === 'STUDENT' || role === 'TEACHER') && (
        <section className="space-y-5">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white px-6 py-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Plans and billing</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
              {role === 'STUDENT' ? 'Student subscription plans' : 'Teacher subscription plans'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Review your current subscription options without leaving this gateway page.
            </p>
          </div>
          {role === 'STUDENT' ? <StudentSubscriptionPlans /> : <TeacherSubscriptionPlans />}
        </section>
      )}
    </div>
  );
};

export default Dashboard;
